import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, setDateFormat, setCurrency } from "@/lib/api";

const AuthContext = createContext(null);

/** Push user's regional preferences into the shared singletons. */
function syncPrefs(u) {
  if (u && typeof u === "object") {
    if (u.date_format) setDateFormat(u.date_format);
    if (u.currency) setCurrency(u.currency);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null = checking, false = no user, object = user
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      syncPrefs(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      syncPrefs(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data.user);
      syncPrefs(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    // Best-effort server logout; local state is cleared regardless so the user
    // is treated as logged out even if the network call fails.
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout API call failed (clearing local state anyway):", e?.message);
    }
    setUser(false);
  };

  const updatePreferences = async (patch) => {
    const { data } = await api.patch("/auth/preferences", patch);
    setUser(data);
    syncPrefs(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, updatePreferences }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
